import { useEffect, useRef, useState, useCallback } from "react";

export const useIntersecting = (options?: IntersectionObserverInit) => {
  const elementRef = useRef<Element | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);

  // Memoize the callback to prevent unnecessary recreations
  const intersectionCallback = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      const intersecting = entry.isIntersecting;
      setIsIntersecting(intersecting);
      if (intersecting && !hasIntersected) {
        setHasIntersected(true);
      }
    });
  }, [hasIntersected]);

  useEffect(() => {
    // Critical: Skip during SSR
    if ( globalThis.window === undefined) return;

    const observerOptions = { threshold: 1, ...options };

    observer.current = new IntersectionObserver(intersectionCallback, observerOptions);

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.current.observe(currentElement);
    }

    return () => {
      observer.current?.disconnect();
    };
  }, [intersectionCallback, options?.root, options?.rootMargin, options?.threshold]);

  return { elementRef, isIntersecting };
};